import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to persist form data in localStorage across page refreshes.
 * Data is automatically saved on every change and cleared on successful submission.
 *
 * @param {string} key - Unique localStorage key for this form
 * @param {object} defaultValues - The initial/default form values
 * @returns {[object, function, function]} - [formData, setFormData, clearSavedData]
 */
export const useFormPersistence = (key, defaultValues) => {
  const storageKey = `nsm_form_${key}`;

  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge saved data with defaults to handle new fields added after save
        return { ...defaultValues, ...parsed };
      }
    } catch (e) {
      console.warn('Failed to load saved form data:', e);
    }
    return defaultValues;
  });

  // Save to localStorage on every change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(formData));
    } catch (e) {
      console.warn('Failed to save form data:', e);
    }
  }, [formData, storageKey]);

  const clearSavedData = useCallback(() => {
    localStorage.removeItem(storageKey);
  }, [storageKey]);

  return [formData, setFormData, clearSavedData];
};

export default useFormPersistence;
